import { useEffect, useMemo, useState } from 'react';
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import Select, { type SingleValue, type StylesConfig } from 'react-select';
import DomainSelector from '@/app/common/components/DomainSelector';
import useDomain from '@/hooks/useDomain';
import { cn } from '@/lib/utils';
import {
  flattenSkillFlowSteps,
  isSkillConfigureAnswersPayload,
  summarizeSkillAnswers,
  visibleSkillFlowSteps,
  withUrlReuseStep,
  type ConfigureSkillToolArgs,
  type SkillFlowChoiceSummary,
  type SkillFlowStep,
  type SkillFlowStepOption
} from './skillFlow.types';
import {
  URL_REUSE_NEW,
  URL_REUSE_SAME,
  URL_REUSE_STEP_ID
} from './priorSkillUrl';
import {
  formatUrlForDisplay,
  isLikelyUrl
} from '../../utils/threadHistoryDisplay';

type Props = {
  id: string;
  title: string;
  description?: string;
  steps: SkillFlowStep[];
  /** Prior page URL from earlier skill runs in this chat. */
  priorUrl?: string | null;
  /** Wrapper class for the domain dropdown. FAQ passes a narrower width. */
  domainSelectClassName?: string;
  onComplete: (answers: Record<string, string[]>) => void;
};

type ReceiptProps = {
  id: string;
  title: string;
  summary: SkillFlowChoiceSummary[];
};

function isDomainStep(step: SkillFlowStep): boolean {
  return step.inputType === 'domain' || step.id === 'domain_slug';
}

function stepValue(
  step: SkillFlowStep,
  answers: Record<string, string[]>
): string {
  if (
    step.inputType === 'textarea' ||
    step.inputType === 'url' ||
    step.inputType === 'text' ||
    isDomainStep(step)
  ) {
    return answers[step.id]?.[0] ?? '';
  }
  return '';
}

function updateTextAnswer(
  answers: Record<string, string[]>,
  stepId: string,
  value: string
): Record<string, string[]> {
  return {
    ...answers,
    [stepId]: value.trim() ? [value] : []
  };
}

function toggleSelectAnswer(
  answers: Record<string, string[]>,
  step: SkillFlowStep,
  optionId: string
): Record<string, string[]> {
  const current = answers[step.id] ?? [];
  if (step.selectionMode === 'multi') {
    const next = current.includes(optionId)
      ? current.filter((id) => id !== optionId)
      : [...current, optionId];
    return { ...answers, [step.id]: next };
  }
  // Optional single-select: click again to clear (sends null when configured).
  if (step.optional && current.includes(optionId)) {
    return { ...answers, [step.id]: [] };
  }
  return { ...answers, [step.id]: [optionId] };
}

function countNonEmptyLines(value: string): string[] {
  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function validateTextareaLines(step: SkillFlowStep, value: string): string | null {
  const hasMin = step.minLines != null && step.minLines > 0;
  const hasMax = step.maxLines != null && step.maxLines > 0;
  if (!hasMin && !hasMax) return null;

  const lines = countNonEmptyLines(value);

  if (hasMin && lines.length < (step.minLines as number)) {
    return `حداقل ${step.minLines} مورد وارد کنید (هر سطر یک کلمه).`;
  }
  if (hasMax && lines.length > (step.maxLines as number)) {
    return `حداکثر ${step.maxLines} مورد مجاز است (هر سطر یک کلمه).`;
  }
  return null;
}

function validateStep(
  step: SkillFlowStep,
  answers: Record<string, string[]>
): string | null {
  if (step.fields) {
    for (const field of step.fields) {
      const fieldError = validateStep(field, answers);
      if (fieldError) return `${field.title}: ${fieldError}`;
    }
    return null;
  }

  if (step.optional) {
    if (step.inputType === 'textarea') {
      const value = stepValue(step, answers).trim();
      if (!value) return null;
      return validateTextareaLines(step, value);
    }
    return null;
  }

  if (step.inputType === 'select' || isDomainStep(step)) {
    if ((answers[step.id] ?? []).length === 0) {
      return isDomainStep(step)
        ? 'یک دامنه را انتخاب کنید.'
        : 'یک گزینه را انتخاب کنید.';
    }
    return null;
  }

  const value = stepValue(step, answers).trim();
  if (!value) {
    return 'این فیلد الزامی است.';
  }
  if (step.inputType === 'url') {
    try {
      // eslint-disable-next-line no-new
      new URL(value);
    } catch {
      return 'آدرس URL معتبر نیست.';
    }
  }
  if (step.inputType === 'textarea') {
    return validateTextareaLines(step, value);
  }
  return null;
}

export function SkillInputFlowReceipt({ id, title, summary }: ReceiptProps) {
  return (
    <div
      className="aui-skill-flow-receipt border-border bg-[var(--muted)]/20 my-5 w-full rounded-2xl border p-4"
      data-skill-flow-id={id}
      dir="rtl"
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="text-sm font-semibold">{title}</div>
        <span className="bg-primary/10 text-primary inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.68rem] font-medium">
          <CheckIcon className="size-3" aria-hidden />
          تکمیل شد
        </span>
      </div>
      <dl className="m-0 space-y-1.5">
        {summary.map((row) => {
          const displayValue = isLikelyUrl(row.value)
            ? formatUrlForDisplay(row.value)
            : row.value;
          const valueDir = isLikelyUrl(row.value) ? 'ltr' : undefined;
          return (
            <div
              key={`${id}-${row.label}`}
              className="text-foreground m-0 text-xs leading-relaxed break-all"
            >
              <dt className="text-foreground inline font-semibold">
                {row.label}:
              </dt>{' '}
              <dd
                className="inline m-0 whitespace-pre-wrap"
                dir={valueDir}
                style={
                  valueDir ? { unicodeBidi: 'isolate' } : undefined
                }
              >
                <ExpandableReceiptValue value={displayValue} />
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}

const RECEIPT_VALUE_PREVIEW_CHARS = 180;

function ExpandableReceiptValue({ value }: { value: string }) {
  const [expanded, setExpanded] = useState(false);
  const needsCollapse = value.length > RECEIPT_VALUE_PREVIEW_CHARS;

  if (!needsCollapse) {
    return <>{value}</>;
  }

  const preview = `${value.slice(0, RECEIPT_VALUE_PREVIEW_CHARS).trimEnd()}…`;

  return (
    <div className="grid gap-1.5">
      <div
        className="max-h-40 overflow-hidden"
        style={expanded ? { maxHeight: 'none' } : undefined}
      >
        {expanded ? value : preview}
      </div>
      <button
        type="button"
        className="mcp-chat-pill-btn mcp-chat-pill-btn--ghost mcp-chat-pill-btn--sm w-fit"
        onClick={() => setExpanded((prev) => !prev)}
      >
        {expanded ? 'نمایش کمتر' : 'نمایش بیشتر'}
      </button>
    </div>
  );
}

function applyUrlReuseChoice(
  answers: Record<string, string[]>,
  steps: SkillFlowStep[],
  choice: string,
  priorUrl: string
): Record<string, string[]> {
  const next: Record<string, string[]> = {
    ...answers,
    [URL_REUSE_STEP_ID]: [choice]
  };

  if (choice === URL_REUSE_SAME) {
    next.url = [priorUrl];
    if (steps.some((step) => step.id === '_source_mode')) {
      next._source_mode = ['url'];
    }
    // HTML path must not leak when reusing a URL.
    if ('html' in next) {
      next.html = [];
    }
    return next;
  }

  if (choice === URL_REUSE_NEW) {
    // Let the user pick source mode / type a new URL.
    if ((next.url?.[0] ?? '') === priorUrl) {
      next.url = [];
    }
    return next;
  }

  return next;
}

const DROPDOWN_SELECT_STYLES: StylesConfig<SkillFlowStepOption, false> = {
  container: (base) => ({ ...base, width: '100%' }),
  control: (base) => ({
    ...base,
    minHeight: 42,
    borderRadius: 12,
    fontSize: '0.875rem',
    boxShadow: 'none'
  }),
  menuPortal: (base) => ({ ...base, zIndex: 1060 }),
  menu: (base) => ({ ...base, fontSize: '0.875rem', zIndex: 30 }),
  option: (base) => ({ ...base, fontSize: '0.875rem' })
};

function shouldUseDropdownSelect(step: SkillFlowStep): boolean {
  if (step.selectionUi === 'dropdown') return true;
  if (step.selectionUi === 'buttons') return false;
  return (step.options?.length ?? 0) > 12;
}

function SkillFlowDropdownSelect({
  step,
  selected,
  onChange
}: {
  step: SkillFlowStep;
  selected: string[];
  onChange: (optionId: string) => void;
}) {
  const options = step.options ?? [];
  const value =
    options.find((option) => option.id === selected[0]) ?? null;

  return (
    <div className="space-y-2">
      <Select<SkillFlowStepOption, false>
        isSearchable
        isClearable={Boolean(step.optional)}
        classNamePrefix="react-select"
        placeholder={step.placeholder ?? 'انتخاب کنید…'}
        noOptionsMessage={() => 'موردی پیدا نشد'}
        menuPlacement="top"
        menuPortalTarget={
          typeof document !== 'undefined' ? document.body : undefined
        }
        maxMenuHeight={220}
        styles={DROPDOWN_SELECT_STYLES}
        options={options}
        getOptionValue={(option) => option.id}
        getOptionLabel={(option) => option.label}
        value={value}
        onChange={(option: SingleValue<SkillFlowStepOption>) => {
          if (!option) {
            if (step.optional && selected[0]) {
              onChange(selected[0]);
            }
            return;
          }
          onChange(option.id);
        }}
      />
      {step.optional ? (
        <p className="text-muted-foreground m-0 text-[0.7rem]">
          اختیاری — می‌توانید بدون انتخاب ادامه دهید.
        </p>
      ) : null}
    </div>
  );
}

/**
 * Tool UI Question Flow–style wizard for chat skill inputs (upfront mode).
 */
export function SkillInputFlow({
  id,
  title,
  description,
  steps,
  priorUrl = null,
  domainSelectClassName = 'position-relative w-100 p-0',
  onComplete
}: Props) {
  const { domain_slug: currentDomainSlug } = useDomain();
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  const [stepIndex, setStepIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const flowSteps = useMemo(
    () => (priorUrl ? withUrlReuseStep(steps, priorUrl) : steps),
    [priorUrl, steps]
  );

  const visibleSteps = useMemo(
    () => visibleSkillFlowSteps(flowSteps, answers),
    [flowSteps, answers]
  );

  const currentStep = visibleSteps[stepIndex];
  const totalSteps = visibleSteps.length;

  // Prefill domain steps with the panel's currently selected domain.
  useEffect(() => {
    if (!currentDomainSlug || currentDomainSlug === 'no-domain') return;
    setAnswers((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const step of flattenSkillFlowSteps(flowSteps)) {
        if (!isDomainStep(step)) continue;
        if ((next[step.id] ?? []).length > 0) continue;
        next[step.id] = [currentDomainSlug];
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [currentDomainSlug, flowSteps]);

  // Prefill select/text defaults from skill configure args (e.g. fa / ir).
  useEffect(() => {
    setAnswers((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const step of flattenSkillFlowSteps(flowSteps)) {
        const defaults = (step.defaultSelectedIds ?? [])
          .map((value) => value.trim())
          .filter(Boolean);
        if (defaults.length === 0) continue;
        if ((next[step.id] ?? []).length > 0) continue;
        next[step.id] = defaults;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [flowSteps]);

  // Keep step index valid when visible steps change (e.g. same → new).
  useEffect(() => {
    if (visibleSteps.length === 0) return;
    setStepIndex((index) => Math.min(index, visibleSteps.length - 1));
  }, [visibleSteps.length]);

  const goNext = () => {
    if (!currentStep) return;
    const validationError = validateStep(currentStep, answers);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    if (stepIndex >= totalSteps - 1) {
      onComplete(answers);
      return;
    }
    setStepIndex((index) => Math.min(index + 1, totalSteps - 1));
  };

  const goBack = () => {
    setError(null);
    setStepIndex((index) => Math.max(index - 1, 0));
  };

  if (!currentStep) {
    return null;
  }

  const renderControl = (step: SkillFlowStep) => {
    const selected = answers[step.id] ?? [];
    return (
      step.inputType === 'select' ? (
        shouldUseDropdownSelect(step) ? (
          <SkillFlowDropdownSelect
            step={step}
            selected={selected}
            onChange={(optionId) => {
              setAnswers((prev) =>
                toggleSelectAnswer(prev, step, optionId)
              );
              setError(null);
            }}
          />
        ) : (
        <div className="flex flex-col gap-2">
          {(step.options ?? []).map((option) => {
            const active = selected.includes(option.id);
            return (
              <button
                key={option.id}
                type="button"
                className={cn(
                  'border-border hover:bg-[var(--muted)]/50 flex w-full items-start justify-between gap-3 rounded-xl border px-3 py-2.5 text-start transition-colors',
                  active && 'border-primary bg-primary/5'
                )}
                onClick={() => {
                  setAnswers((prev) => {
                    if (
                      step.id === URL_REUSE_STEP_ID &&
                      priorUrl
                    ) {
                      return applyUrlReuseChoice(
                        prev,
                        flowSteps,
                        option.id,
                        priorUrl
                      );
                    }
                    return toggleSelectAnswer(prev, step, option.id);
                  });
                  setError(null);
                }}
              >
                <span className="min-w-0">
                  {step.id === URL_REUSE_STEP_ID ? (
                    <span className="block text-sm leading-snug">
                      {option.label}
                      {option.description ? (
                        <span
                          className="text-foreground/85 ms-1 break-all font-normal [unicode-bidi:isolate]"
                          dir={
                            option.id === URL_REUSE_SAME ? 'ltr' : undefined
                          }
                        >
                          ({option.id === URL_REUSE_SAME
                            ? formatUrlForDisplay(option.description)
                            : option.description}
                          )
                        </span>
                      ) : null}
                    </span>
                  ) : (
                    <>
                      <span className="block text-sm">{option.label}</span>
                      {option.description ? (
                        <span className="text-muted-foreground mt-0.5 block text-xs">
                          {option.description}
                        </span>
                      ) : null}
                    </>
                  )}
                </span>
                {active ? (
                  <CheckIcon
                    className="text-primary mt-0.5 size-4 shrink-0"
                    aria-hidden
                  />
                ) : null}
              </button>
            );
          })}
          {step.optional ? (
            <p className="text-muted-foreground m-0 text-[0.7rem]">
              اختیاری — می‌توانید بدون انتخاب ادامه دهید
              {selected.length > 0 ? ' یا با کلیک دوباره انتخاب را بردارید' : ''}
              .
            </p>
          ) : null}
        </div>
        )
      ) : isDomainStep(step) ? (
        <DomainSelector
          selectOnly
          menuPlacement="top"
          className={domainSelectClassName}
          value={stepValue(step, answers) || null}
          onChange={(domain) => {
            setAnswers((prev) =>
              updateTextAnswer(prev, step.id, domain.slug)
            );
            setError(null);
          }}
        />
      ) : step.inputType === 'textarea' ? (
        <div className="space-y-1.5">
          <textarea
            className="border-input bg-[var(--background)] text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] min-h-32 w-full rounded-xl border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
            value={stepValue(step, answers)}
            onChange={(event) => {
              setAnswers((prev) =>
                updateTextAnswer(prev, step.id, event.target.value)
              );
              setError(null);
            }}
            placeholder={
              step.placeholder ??
              (step.minLines != null || step.maxLines != null
                ? 'هر سطر یک کلمه'
                : 'HTML صفحه را اینجا بچسبانید…')
            }
            dir={
              step.minLines != null || step.maxLines != null
                ? 'rtl'
                : 'ltr'
            }
            style={
              step.minLines != null || step.maxLines != null
                ? { textAlign: 'right' }
                : undefined
            }
          />
          {step.minLines != null || step.maxLines != null ? (
            <p className="text-muted-foreground m-0 text-[0.7rem]">
              هر سطر یک کلمه
              {step.minLines != null
                ? ` · حداقل ${step.minLines}`
                : ''}
              {step.maxLines != null
                ? ` · حداکثر ${step.maxLines}`
                : ''}
            </p>
          ) : null}
        </div>
      ) : (
        <input
          type={step.inputType === 'url' ? 'url' : 'text'}
          className="border-input bg-[var(--background)] text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] h-10 w-full rounded-xl border px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
          value={stepValue(step, answers)}
          onChange={(event) => {
            setAnswers((prev) =>
              updateTextAnswer(prev, step.id, event.target.value)
            );
            setError(null);
          }}
          placeholder={
            step.placeholder ??
            (step.inputType === 'url'
              ? 'https://example.com/page'
              : undefined)
          }
          dir={step.inputType === 'url' ? 'ltr' : 'rtl'}
        />
      )
    );
  };

  return (
    <div
      className="aui-skill-input-flow border-border bg-[var(--card)] text-[var(--card-foreground)] my-5 w-full overflow-hidden rounded-2xl border shadow-sm"
      data-skill-flow-id={id}
      dir="rtl"
    >
      <div className="border-border/70 border-b px-4 py-3">
        <div className="text-muted-foreground mb-1 text-[0.72rem]">
          مرحله {stepIndex + 1} از {totalSteps}
        </div>
        <div className="text-sm font-semibold">{title}</div>
        {description ? (
          <p className="text-muted-foreground mt-1 mb-0 text-xs leading-relaxed">
            {description}
          </p>
        ) : null}
      </div>

      <div className="space-y-3 px-4 py-4">
        <div>
          <div className="mb-1 text-sm font-medium">{currentStep.title}</div>
          {currentStep.description ? (
            <p className="text-muted-foreground mb-2 text-xs leading-relaxed">
              {currentStep.description}
            </p>
          ) : null}
        </div>

        {currentStep.fields ? (
          <div className="space-y-4">
            {currentStep.fields.map((field) => (
              <div key={field.id}>
                <div className="mb-1.5 text-xs font-medium">{field.title}</div>
                {field.description ? (
                  <p className="text-muted-foreground mb-2 text-xs leading-relaxed">
                    {field.description}
                  </p>
                ) : null}
                {renderControl(field)}
              </div>
            ))}
          </div>
        ) : (
          renderControl(currentStep)
        )}

        {error ? (
          <p className="text-destructive m-0 text-xs">{error}</p>
        ) : null}
      </div>

      <div className="border-border/70 flex items-center justify-between gap-2 border-t px-4 py-3">
        <button
          type="button"
          className="mcp-chat-pill-btn mcp-chat-pill-btn--ghost"
          disabled={stepIndex === 0}
          onClick={goBack}
        >
          <ChevronRightIcon aria-hidden />
          قبلی
        </button>
        <button
          type="button"
          className="mcp-chat-pill-btn mcp-chat-pill-btn--primary"
          onClick={goNext}
        >
          {stepIndex >= totalSteps - 1 ? 'ادامه' : 'بعدی'}
          {stepIndex < totalSteps - 1 ? (
            <ChevronLeftIcon aria-hidden />
          ) : null}
        </button>
      </div>
    </div>
  );
}

export function skillFlowReceiptFromResult(
  args: ConfigureSkillToolArgs,
  result: unknown
) {
  if (!isSkillConfigureAnswersPayload(result)) {
    return null;
  }
  return {
    id: args.id,
    title: args.title,
    summary: summarizeSkillAnswers(args.steps, result.answers)
  };
}
